import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS, ROUTE_ACCESS } from "@/contexts/AuthContext";
import EmpresaSwitcher from "@/components/EmpresaSwitcher";
import { useEmpresa } from "@/contexts/EmpresaContext";
import {
  Gauge,
  Buildings,
  MapPin,
  UsersThree,
  Clock,
  CalendarBlank,
  Warning,
  CloudArrowUp,
  ChartBar,
  GearSix,
  CalendarX,
  ListChecks,
  IdentificationBadge,
  CreditCard,
  SignOut,
  House,
  X,
  UserCircle,
  GraduationCap,
  Chalkboard,
  ChalkboardTeacher,
} from "@phosphor-icons/react";

const ALL_ITEMS = [
  { to: "/app/dashboard", label: "Dashboard", icon: Gauge, allow: ROUTE_ACCESS.dashboard },
  { to: "/app/portal", label: "Mi Portal", icon: House, allow: ROUTE_ACCESS.portal },
  { to: "/app/empresas", label: "Empresas", icon: Buildings, allow: ROUTE_ACCESS.empresas },
  { to: "/app/centros", label: "Centros de Trabajo", icon: MapPin, allow: ROUTE_ACCESS.centros },
  { to: "/app/empleados", label: "Empleados", icon: UsersThree, allow: ROUTE_ACCESS.empleados },
  { to: "/app/horarios", label: "Horarios", icon: Clock, allow: ROUTE_ACCESS.horarios },
  { to: "/app/escuela", label: "Escuela", icon: GraduationCap, allow: ROUTE_ACCESS.escuela },
  { to: "/app/carga-docente", label: "Carga docente", icon: Chalkboard, allow: ROUTE_ACCESS.cargaDocente },
  { to: "/app/asistencia-docente", label: "Asistencia docente", icon: ChalkboardTeacher, allow: ROUTE_ACCESS.asistenciaDocente },
  { to: "/app/jornadas", label: "Jornadas", icon: CalendarBlank, allow: ROUTE_ACCESS.jornadas },
  { to: "/app/incidencias", label: "Incidencias", icon: Warning, allow: ROUTE_ACCESS.incidencias },
  { to: "/app/checadores", label: "Checadores", icon: CloudArrowUp, allow: ROUTE_ACCESS.checadores },
  { to: "/app/reportes", label: "Reportes", icon: ChartBar, allow: ROUTE_ACCESS.reportes },
  { to: "/app/configuracion", label: "Configuración Normativa", icon: GearSix, allow: ROUTE_ACCESS.configuracion },
  { to: "/app/calendario", label: "Calendario Laboral", icon: CalendarX, allow: ROUTE_ACCESS.calendario },
  { to: "/app/auditoria", label: "Auditoría", icon: ListChecks, allow: ROUTE_ACCESS.auditoria },
  { to: "/app/usuarios", label: "Usuarios y Permisos", icon: IdentificationBadge, allow: ROUTE_ACCESS.usuarios },
  { to: "/app/suscripcion", label: "Suscripción", icon: CreditCard, allow: ROUTE_ACCESS.suscripcion },
];

export default function Sidebar({ open = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const { esEscuela } = useEmpresa();
  const navigate = useNavigate();

  const items = ALL_ITEMS.filter((it) => it.allow(user, { esEscuela }));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-30 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-backdrop"
          aria-hidden="true"
        />
      )}
      <aside
        data-testid="sidebar"
        className={`w-64 shrink-0 fixed left-0 top-0 bottom-0 z-40 bg-slate-50 border-r border-slate-200 flex flex-col
          transform transition-transform duration-200 ease-out
          lg:translate-x-0
          ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Brand */}
        <div className="px-6 pt-7 pb-6 border-b border-slate-200 flex items-center justify-between">
          <img
            src="/brand/logotipo.png"
            alt="CHKR — Jornada Electrónica"
            className="h-10 w-auto"
          />
          <button
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-slate-700 p-1"
            aria-label="Cerrar menú"
            data-testid="sidebar-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Empresa activa (solo super_admin) */}
        <EmpresaSwitcher />

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={onClose}
                data-testid={`nav-${it.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-brand text-white font-semibold"
                      : "text-slate-700 hover:bg-brand-accent-soft hover:text-brand-accent font-medium"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User box */}
        <div className="px-4 py-4 border-t border-slate-200">
          <NavLink
            to="/app/perfil"
            onClick={onClose}
            data-testid="sidebar-user-link"
            className="flex items-center gap-3 mb-3 rounded-md px-1 py-1 -mx-1 hover:bg-slate-200/70 transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm shrink-0">
              {(user?.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink truncate" data-testid="sidebar-user-name">
                {user?.name}
              </div>
              <div className="text-[10px] tracking-wider uppercase text-slate-500 font-bold">
                {ROLE_LABELS[user?.role] || user?.role}
              </div>
            </div>
            <UserCircle size={18} weight="duotone" className="text-slate-400 shrink-0" />
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-md hover:bg-white hover:border-slate-300 transition-all"
          >
            <SignOut size={16} weight="bold" />
            Cerrar sesión
          </button>
        </div>
      </aside>
    </>
  );
}
